/*
 * Description:
 * ---------------------
 * Fleet Overview — tile-based view of every registered edge node.
 * Click a tile to smoothly expand full diagnostics inline.
 *
 * ! file NodeSummaryPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  FiCpu,
  FiActivity,
  FiWifi,
  FiMonitor,
  FiHardDrive,
  FiGlobe,
  FiClock,
  FiArrowUp,
  FiArrowDown,
  FiX,
  FiLayers,
  FiSearch,
  FiFilter,
} from "react-icons/fi";
import { fmtBytes, fmtNum, ago } from "../utils/formatters";
import { SkeletonTileGrid } from "./Skeleton";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanNamesProvider, WanLabel } from "./WanNames";
import { isMobileNode, nodeKindLabel } from "../utils/nodeKind";

// ── Edge Controller icon (inline SVG for a compact box with antenna) ─────────
function EdgeIcon({ size = 28, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Rounded box body */}
      <rect x="4" y="10" width="20" height="14" rx="3" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5" />
      {/* Antenna mast */}
      <line x1="14" y1="10" x2="14" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Signal arcs */}
      <path d="M10 6.5a5.5 5.5 0 0 1 8 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
      <path d="M11.5 8a3 3 0 0 1 5 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
      {/* LED dots */}
      <circle cx="9" cy="20" r="1.2" fill="currentColor" opacity="0.6" />
      <circle cx="14" cy="20" r="1.2" fill="currentColor" opacity="0.6" />
      <circle cx="19" cy="20" r="1.2" fill="currentColor" opacity="0.6" />
    </svg>
  );
}

// ── Mobile phone icon (Android tunnel client) ────────────────────────────────
function MobileIcon({ size = 28, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Phone body */}
      <rect x="7" y="3" width="14" height="22" rx="3" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5" />
      {/* Top speaker */}
      <line x1="12" y1="6" x2="16" y2="6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      {/* Bottom home indicator */}
      <line x1="12" y1="22" x2="16" y2="22" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      {/* Signal arcs in the screen area */}
      <path d="M11 13.5a4 4 0 0 1 6 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
      <path d="M12.5 15.5a2 2 0 0 1 3 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
      <circle cx="14" cy="17.5" r="0.7" fill="currentColor" />
    </svg>
  );
}

function NodeIcon({ node, size = 28, className = "" }) {
  return isMobileNode(node)
    ? <MobileIcon size={size} className={className} />
    : <EdgeIcon size={size} className={className} />;
}

const extractEdgeTunIp = (apiUrl) => {
  if (!apiUrl) return null;
  const match = apiUrl.match(/\/proxy\/([\d.]+)/);
  return match ? match[1] : null;
};

// Compute the /30 pair consumed by a node: its own IP and the cloud IP (own - 1).
// Each TUN /30 block is: .0 network, .1 cloud, .2 node, .3 broadcast.
// So for any node IP, the cloud-side counterpart is IP - 1. `self` names the
// node's end: an edge, or a phone, which holds a /30 the same way.
const computeOccupiedIps = (registeredTunAddr, activeTunIp, self = "Edge") => {
  // Prefer the active (real) TUN IP; fall back to the registered address
  const rawIp = activeTunIp || (registeredTunAddr ? registeredTunAddr.replace(/\/\d+$/, "") : null);
  if (!rawIp) return [];

  const parts = rawIp.split(".").map(Number);
  if (parts.length !== 4) return [{ ip: rawIp, role: self }];

  // Cloud-side = edge IP - 1 in the /30
  const cloudParts = [...parts];
  cloudParts[3] -= 1;
  const cloudIp = cloudParts.join(".");

  const ips = [
    { ip: cloudIp, role: "Cloud" },
    { ip: rawIp, role: self },
  ];

  // Flag if registered address differs from active (sync issue)
  const regIp = registeredTunAddr ? registeredTunAddr.replace(/\/\d+$/, "") : null;
  if (regIp && regIp !== rawIp) {
    ips.push({ ip: regIp, role: "Registered", mismatch: true });
  }

  return ips;
};

// ── Detail drawer (shown when a tile is selected) ────────────────────────────

function NodeDetail({ node, proxyBase, onClose }) {
  const mobile = isMobileNode(node);
  const isStale = node.stale === true;
  // A phone answers /diagnostics over its channel while its tunnel is up. One
  // the server lists as offline has nothing to answer with, and asking would
  // only wait out a timeout.
  const askable = !(mobile && isStale);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(askable);
  const [error, setError] = useState("");
  const timerRef = useRef(null);

  const edgeTunIp = useMemo(
    () => extractEdgeTunIp(node.api_url) ||
      (node.tun_address ? String(node.tun_address).replace(/\/\d+$/, "") : null),
    [node.api_url, node.tun_address]
  );
  const occupiedIps = useMemo(
    () => computeOccupiedIps(node.tun_address, edgeTunIp, mobile ? "Phone" : "Edge"),
    [node.tun_address, edgeTunIp, mobile]
  );

  const diagUrl = useMemo(() => {
    if (!askable || !node.node_id || !proxyBase) return null;
    return `${proxyBase}/node/${encodeURIComponent(node.node_id)}/diagnostics`;
  }, [askable, node.node_id, proxyBase]);

  const fetchDiag = useCallback(async () => {
    if (!diagUrl) return;
    const MAX_RETRIES = 2;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await fetchWithTimeout(diagUrl);
        if (!res.ok) {
          if (attempt < MAX_RETRIES && (res.status === 502 || res.status === 503 || res.status === 504)) {
            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
            continue;
          }
          throw new Error(`${res.status}`);
        }
        setDiag(await res.json());
        setError("");
        return;
      } catch (err) {
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
          continue;
        }
        setError(err.message || "Unreachable");
      } finally {
        setLoading(false);
      }
    }
  }, [diagUrl]);

  useEffect(() => {
    if (!askable) return;
    setLoading(true);
    fetchDiag();
    timerRef.current = setInterval(fetchDiag, 6000);
    return () => clearInterval(timerRef.current);
  }, [askable, fetchDiag]);

  const sys = diag?.system;
  const tunnel = diag?.tunnel;
  const tc = diag?.tc_classifier;
  const deviceCount = diag?.devices?.length || 0;
  const activeDevices = useMemo(() => {
    if (!diag?.devices) return 0;
    return diag.devices.filter((d) => (d.active_seconds ?? 0) <= 60).length;
  }, [diag]);
  const wanList = useMemo(() => {
    if (!diag?.wans) return [];
    return Object.values(diag.wans).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [diag]);
  const carrying = wanList.filter((w) => w.active).length;

  return (
    <div className="fleet-detail">
      {/* ── Header ── */}
      <div className="fleet-detail-head">
        <div className="fleet-detail-head-left">
          <div className={`fleet-detail-icon ${isStale ? "stale" : ""}`}>
            <NodeIcon node={node} size={32} />
          </div>
          <div>
            <h2 className="fleet-detail-name">{node.node_id}</h2>
            <span className="fleet-detail-sub">
              {nodeKindLabel(node)} · Last seen {ago(node.last_seen)}
            </span>
          </div>
        </div>
        <button className="fleet-detail-close" onClick={onClose} title="Close">
          <FiX size={18} />
        </button>
      </div>

      {/* ── A phone that is not connected ── */}
      {!askable && (
        <div className="fleet-detail-body">
          <div className="fleet-facts">
            <Fact icon={FiGlobe} label="TUN IP" value={edgeTunIp || "—"} mono />
            <Fact icon={FiClock} label="Last Seen" value={ago(node.last_seen)} />
            <Fact icon={FiMonitor} label="Type" value="Phone" />
            <Fact icon={FiActivity} label="Status" value="Offline" accent="#ef4444" />
          </div>
          <Section title="About this phone">
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, opacity: 0.75 }}>
              A phone answers only while its tunnel is up. What it carried while
              connected is under Pilot Insights.
            </p>
          </Section>
        </div>
      )}

      {/* ── Loading / Error ── */}
      {askable && loading && !diag && (
        <div className="fleet-detail-placeholder">
          <div className="fleet-spinner" />
          <span>Fetching diagnostics…</span>
        </div>
      )}
      {askable && error && !diag && (
        <div className="fleet-detail-error">
          <FiActivity size={14} />{" "}
          {mobile && node.channel === false
            ? "Connected, but its app is older than the console's view of phones. Update the app on the phone."
            : `Unable to reach node — ${error}`}
        </div>
      )}

      {/* ── Content ── */}
      {diag && (
        <div className="fleet-detail-body">
          {/* ── Quick facts row ── */}
          <div className="fleet-facts">
            <Fact icon={FiGlobe} label="TUN IP" value={edgeTunIp || "—"} mono />
            {mobile ? (
              <>
                <Fact icon={FiClock} label="Connected For" value={diag.uptime || "—"} />
                <Fact icon={FiWifi} label="Links" value={`${carrying} of ${wanList.length} carrying`} />
                <Fact icon={FiActivity} label="Status" value="Online" accent="#22c55e" />
              </>
            ) : (
              <>
                <Fact icon={FiClock} label="Uptime" value={sys?.uptime || "—"} />
                <Fact
                  icon={FiMonitor}
                  label="Devices"
                  value={`${activeDevices} active / ${deviceCount}`}
                />
                <Fact
                  icon={FiLayers}
                  label="Classifier"
                  value={tc?.alive ? `${fmtNum(tc.flows)} flows` : "Off"}
                  accent={tc?.alive ? "#22c55e" : "#ef4444"}
                />
              </>
            )}
          </div>

          {!mobile && (
            <>
              {/* ── Connection Mode ── */}
              <Section title="Connection Mode">
                <ModeSwitch node={node} proxyBase={proxyBase} />
              </Section>

              {/* ── Bonding Mode ── */}
              <Section title="Bonding mode">
                <BondingSummary node={node} proxyBase={proxyBase} />
              </Section>
            </>
          )}

          {/* ── IP Addressing ── */}
          <Section title="IP Addressing">
            <div className="fleet-ip-row">
              {occupiedIps.filter((e) => !e.mismatch).map((e) => (
                <div key={e.ip} className="fleet-ip-chip">
                  <span className="fleet-ip-role">{e.role}</span>
                  <span className="fleet-ip-addr">{e.ip}</span>
                </div>
              ))}
              <div className="fleet-ip-chip fleet-ip-total">
                <span className="fleet-ip-role">Consumed</span>
                <span className="fleet-ip-addr">{occupiedIps.filter((e) => !e.mismatch).length} IPs</span>
              </div>
            </div>
            {occupiedIps.some((e) => e.mismatch) && (
              <>
                <div className="fleet-ip-row" style={{ marginTop: 8 }}>
                  {occupiedIps.filter((e) => e.mismatch).map((e) => (
                    <div
                      key={e.ip}
                      className="fleet-ip-chip fleet-ip-mismatch"
                      title="Server registry differs from active tunnel IP — needs re-sync"
                    >
                      <span className="fleet-ip-role">{e.role}</span>
                      <span className="fleet-ip-addr">{e.ip}</span>
                    </div>
                  ))}
                </div>
                <p className="fleet-ip-warning">
                  ⚠ Server registry TUN address differs from the active tunnel IP.
                  Redeploy the server and edge to sync.
                </p>
              </>
            )}
            {occupiedIps.some((e) => e.role === "Registered") && (
              <p className="fleet-ip-warning">
                ⚠ Registered TUN address differs from the active tunnel IP.
                The server registry may be out of sync.
              </p>
            )}
          </Section>

          {/* ── System ── */}
          {sys && (
            <Section title="System">
              <div className="fleet-kv-grid">
                <KV label="CPUs" value={fmtNum(sys.num_cpu)} />
                <KV label="Heap" value={sys.heap_mb ? `${sys.heap_mb.toFixed(1)} MB` : "—"} />
                <KV label="Goroutines" value={fmtNum(sys.goroutines)} />
                <KV label="IP Forward" value={sys.ip_forward ? "Yes" : "No"} ok={sys.ip_forward} />
                <KV label="Restarts" value={fmtNum(sys.restart_count)} />
              </div>
            </Section>
          )}

          {/* ── Tunnel ── */}
          {tunnel && (
            <Section title="Tunnel">
              <div className="fleet-tunnel-row">
                <TunnelStat icon={FiArrowUp} label="TX" bytes={tunnel.tx_bytes} pkts={tunnel.tx_packets} />
                <TunnelStat icon={FiArrowDown} label="RX" bytes={tunnel.rx_bytes} pkts={tunnel.rx_packets} />
                <div className={`fleet-tunnel-errors ${tunnel.errors > 0 ? "has" : ""}`}>
                  {tunnel.errors > 0 ? `${fmtNum(tunnel.errors)} errors` : "0 errors"}
                </div>
              </div>
            </Section>
          )}

          {/* ── WAN Links ── */}
          {wanList.length > 0 && (
            <Section title="WAN Links">
              <div className="fleet-wan-grid">
                {wanList.map((w) => (
                  <WanCard key={w.name} wan={w} />
                ))}
              </div>
            </Section>
          )}

          {mobile && (
            <Section title="About this phone">
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, opacity: 0.75 }}>
                The console reads a phone and does not manage it: its settings
                are changed on the phone itself.
              </p>
            </Section>
          )}

          {/* ── Devices ── */}
          {diag.devices && diag.devices.length > 0 && (
            <Section title="Connected Devices">
              <div className="fleet-dev-list">
                {diag.devices.map((dev, i) => (
                  <DeviceRow key={dev.mac || dev.ip || i} dev={dev} />
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Tiny sub-components ── */

function Fact({ icon: Icon, label, value, mono, accent }) {
  return (
    <div className="fleet-fact">
      <div className="fleet-fact-icon" style={accent ? { color: accent } : undefined}>
        <Icon size={16} />
      </div>
      <div className="fleet-fact-body">
        <span className="fleet-fact-label">{label}</span>
        <span className={`fleet-fact-value ${mono ? "mono" : ""}`}>{value}</span>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="fleet-section">
      <div className="fleet-section-title">{title}</div>
      {children}
    </div>
  );
}

// ── Bonding-mode summary ─────────────────────────────────────────────────────
// Read-only per-category view of the edge's scheduling action (SPREAD vs
// FAILOVER), fetched from the edge's GET /policy through the same reverse proxy
// used for diagnostics (`/node/<id>/policy`). Surfaces at-a-glance whether
// traffic bonding is active on a node from the Fleet Overview without opening
// the Bonding panel. Every class spreads by default; applying a QoS quality
// ceiling to a class switches it to failover.
const BONDING_CATEGORIES = ["BE", "CRITICAL", "REALTIME", "INTERACTIVE", "STREAMING", "BULK", "BACKGROUND", "DIAG"];

function BondingSummary({ node, proxyBase }) {
  const [actions, setActions] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!proxyBase || !node.node_id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchWithTimeout(
          `${proxyBase}/node/${encodeURIComponent(node.node_id)}/policy`
        );
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const map = {};
        for (const r of (data?.rules || [])) {
          if (r && r.class) map[r.class] = r.action || "bond";
        }
        setActions(map);
        setErr("");
      } catch (e) {
        if (!cancelled) setErr(e.message === "Failed to fetch" ? "Unreachable" : e.message || "Failed");
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [proxyBase, node.node_id]);

  if (err && !actions) {
    return <span style={{ fontSize: 12, opacity: 0.6 }}>Bonding state unavailable — {err}</span>;
  }
  if (!actions) {
    return <span style={{ fontSize: 12, opacity: 0.6 }}>Loading bonding state…</span>;
  }

  // "spread"/"stripe" are the retired mode names; an edge on an older build can
  // still report them and they mean the same single bonding action.
  const isBonded = (a) => ["bond", "spread", "stripe"].includes(a ?? "bond");
  const spreadCount = BONDING_CATEGORIES.filter((c) => isBonded(actions[c])).length;
  const total = BONDING_CATEGORIES.length;
  const allSpread = spreadCount === total;
  const noneSpread = spreadCount === 0;
  const summaryLabel = allSpread
    ? "Bonding: all classes bonded"
    : noneSpread
    ? "Bonding: off (all failover)"
    : `Bonding: ${spreadCount}/${total} classes bonded`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            padding: "2px 10px",
            borderRadius: 999,
            color: noneSpread ? "rgb(148,163,184)" : "rgb(34,197,94)",
            background: noneSpread ? "rgba(148,163,184,0.12)" : "rgba(34,197,94,0.12)",
            border: `1px solid ${noneSpread ? "rgba(148,163,184,0.4)" : "rgba(34,197,94,0.4)"}`,
          }}
        >
          {summaryLabel}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {BONDING_CATEGORIES.map((c) => {
          const spread = (actions[c] ?? "spread") === "spread";
          return (
            <span
              key={c}
              title={spread ? `${c}: spreading across all live WANs` : `${c}: failover (single quality-steered WAN)`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11.5,
                fontWeight: 600,
                padding: "3px 9px",
                borderRadius: 7,
                color: spread ? "rgb(22,101,52)" : "rgb(100,116,139)",
                background: spread ? "rgba(34,197,94,0.10)" : "rgba(148,163,184,0.10)",
                border: `1px solid ${spread ? "rgba(34,197,94,0.35)" : "rgba(148,163,184,0.3)"}`,
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: spread ? "rgb(34,197,94)" : "rgb(148,163,184)" }} />
              {c}
            </span>
          );
        })}
      </div>
      <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, opacity: 0.6 }}>
        Spread bonds a class's flows across all live WANs; failover keeps it on one quality-steered WAN.
        Manage per class from the Bonding panel.
      </p>
    </div>
  );
}

// ── Connection-mode switch ───────────────────────────────────────────────────
// Lets an operator flip an edge between "anchored" (bonded tunnel via the cloud
// anchor) and "direct" (kernel-route NAT, no tunnel). The POST reaches the edge
// through the same notabox-server reverse proxy used for diagnostics
// (`/node/<id>/mode` → server → edge `POST /mode`). The edge launches an
// out-of-process systemd swap, so the request returns immediately (202) while
// the binary is replaced; the node re-registers in the new mode within a few
// seconds and the fleet badge updates on the next poll.
function ModeSwitch({ node, proxyBase }) {
  const current = (node.mode || "anchored").toLowerCase() === "direct" ? "direct" : "anchored";
  const target = current === "direct" ? "anchored" : "direct";
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const targetCfg =
    target === "direct"
      ? { color: "rgb(252,211,77)", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)" }
      : { color: "rgb(125,211,252)", bg: "rgba(56,189,248,0.12)", border: "rgba(56,189,248,0.4)" };

  const submit = useCallback(async () => {
    if (!proxyBase || !node.node_id) {
      setErr("No proxy route to this node.");
      return;
    }
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const res = await fetchWithTimeout(
        `${proxyBase}/node/${encodeURIComponent(node.node_id)}/mode`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode: target }),
        },
        8000
      );
      if (!res.ok) throw new Error(`${res.status}`);
      setMsg(
        `Switching to ${target}. The edge restarts in the new mode — it will reappear shortly.`
      );
      setConfirming(false);
    } catch (e) {
      setErr(e.message === "Failed to fetch" ? "Unreachable" : e.message || "Failed");
    } finally {
      setBusy(false);
    }
  }, [proxyBase, node.node_id, target]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, opacity: 0.7 }}>Current:</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "2px 10px",
            borderRadius: 999,
            textTransform: "capitalize",
            color: current === "direct" ? "rgb(252,211,77)" : "rgb(125,211,252)",
            background: current === "direct" ? "rgba(245,158,11,0.12)" : "rgba(56,189,248,0.12)",
            border: `1px solid ${current === "direct" ? "rgba(245,158,11,0.4)" : "rgba(56,189,248,0.4)"}`,
          }}
        >
          {current}
        </span>
      </div>

      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, opacity: 0.7 }}>
        {target === "direct"
          ? "Direct mode bypasses the cloud anchor: traffic egresses straight out the active WAN with NAT — lower latency, but the public IP becomes the local WAN IP and bonding/failover-without-drop is disabled."
          : "Anchored mode bonds all WANs through the cloud anchor for a stable public IP and session-preserving failover."}
      </p>

      {!confirming && !msg && (
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(true)}
          style={{
            alignSelf: "flex-start",
            fontSize: 13,
            fontWeight: 600,
            padding: "7px 14px",
            borderRadius: 8,
            cursor: "pointer",
            color: targetCfg.color,
            background: targetCfg.bg,
            border: `1px solid ${targetCfg.border}`,
            textTransform: "capitalize",
          }}
        >
          Switch to {target}
        </button>
      )}

      {confirming && !msg && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: 12,
            borderRadius: 8,
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
          }}
        >
          <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            ⚠ Switching to <strong style={{ textTransform: "capitalize" }}>{target}</strong> briefly
            drops this edge's connectivity and resets active sessions (the egress public IP changes).
            Continue?
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={busy}
              onClick={submit}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: "6px 14px",
                borderRadius: 8,
                cursor: busy ? "default" : "pointer",
                color: "#fff",
                background: busy ? "rgba(239,68,68,0.5)" : "rgb(220,38,38)",
                border: "1px solid rgba(239,68,68,0.6)",
                textTransform: "capitalize",
              }}
            >
              {busy ? "Switching…" : `Confirm switch to ${target}`}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: "6px 14px",
                borderRadius: 8,
                cursor: "pointer",
                color: "inherit",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            padding: 10,
            borderRadius: 8,
            color: "rgb(134,239,172)",
            background: "rgba(34,197,94,0.1)",
            border: "1px solid rgba(34,197,94,0.35)",
          }}
        >
          {msg}
        </div>
      )}

      {err && (
        <div
          style={{
            fontSize: 12.5,
            color: "rgb(252,165,165)",
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderRadius: 8,
            padding: 10,
          }}
        >
          Switch failed — {err}
        </div>
      )}
    </div>
  );
}

function KV({ label, value, ok }) {
  return (
    <div className="fleet-kv">
      <span className="fleet-kv-label">{label}</span>
      <span className={`fleet-kv-value ${ok === true ? "ok" : ok === false ? "err" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function TunnelStat({ icon: Icon, label, bytes, pkts }) {
  return (
    <div className="fleet-tunnel-stat">
      <Icon size={14} className="fleet-tunnel-icon" />
      <span className="fleet-tunnel-label">{label}</span>
      <span className="fleet-tunnel-bytes">{fmtBytes(bytes)}</span>
      <span className="fleet-tunnel-pkts">{fmtNum(pkts)} pkts</span>
    </div>
  );
}

function WanCard({ wan }) {
  const up = wan.active !== false;
  return (
    <div className={`fleet-wan-card ${up ? "" : "down"}`}>
      <div className="fleet-wan-head">
        <span className={`fleet-wan-led ${up ? "on" : "off"}`} />
        <span className="fleet-wan-name"><WanLabel iface={wan.name} /></span>
      </div>
      <div className="fleet-wan-stats">
        <span>↑ {fmtBytes(wan.tx_bytes)}</span>
        <span>↓ {fmtBytes(wan.rx_bytes)}</span>
      </div>
    </div>
  );
}

function DeviceRow({ dev }) {
  const active = (dev.active_seconds ?? 999) <= 60;
  return (
    <div className={`fleet-dev ${active ? "active" : "stale"}`}>
      <span className="fleet-dev-ip">{dev.ip || "—"}</span>
      <span className="fleet-dev-host">{dev.hostname || dev.mac || "—"}</span>
      <span className="fleet-dev-traffic">
        ↑{fmtBytes(dev.tx_bytes)} ↓{fmtBytes(dev.rx_bytes)}
      </span>
      <span className={`fleet-dev-badge ${active ? "on" : "off"}`}>
        {active ? "Active" : "Idle"}
      </span>
    </div>
  );
}

// ── Tile ─────────────────────────────────────────────────────────────────────

function NodeTile({ node, isSelected, onSelect }) {
  const edgeTunIp = useMemo(
    () => extractEdgeTunIp(node.api_url) ||
      (node.tun_address ? String(node.tun_address).replace(/\/\d+$/, "") : null),
    [node.api_url, node.tun_address]
  );
  const isStale = node.stale === true;
  const mobile = isMobileNode(node);
  // Mode badge: anchored (bonded tunnel) vs direct (kernel-route NAT).
  // Not shown for mobile clients, which don't run in either edge mode.
  const isDirect = (node.mode || "anchored").toLowerCase() === "direct";

  return (
    <button
      type="button"
      className={`fleet-tile ${isSelected ? "selected" : ""} ${isStale ? "stale" : ""} ${mobile ? "mobile" : ""}`}
      onClick={() => onSelect(node.node_id)}
    >
      {/* Status LED */}
      <span className={`fleet-tile-led ${isStale ? "off" : "on"}`} />

      {/* Icon */}
      <div className={`fleet-tile-icon ${isStale ? "stale" : ""}`}>
        <NodeIcon node={node} size={36} />
      </div>

      {/* Name */}
      <span className="fleet-tile-name">{node.node_id}</span>

      {/* Kind tag (mobile only — edge tiles stay clean) */}
      {mobile && <span className="fleet-tile-kind">Mobile</span>}

      {/* Mode badge (edge nodes only) */}
      {!mobile && (
        <span
          style={{
            marginTop: 4,
            padding: "1px 8px",
            borderRadius: 9999,
            fontSize: "0.62rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            border: isDirect
              ? "1px solid rgba(245, 158, 11, 0.45)"
              : "1px solid rgba(56, 189, 248, 0.45)",
            color: isDirect ? "rgb(252, 211, 77)" : "rgb(125, 211, 252)",
            background: isDirect
              ? "rgba(245, 158, 11, 0.10)"
              : "rgba(56, 189, 248, 0.10)",
          }}
          title={
            isDirect
              ? "Direct mode — bypasses the cloud anchor (no tunnel/bonding)"
              : "Anchored — bonded tunnel through the cloud anchor"
          }
        >
          {isDirect ? "Direct" : "Anchored"}
        </span>
      )}

      {/* TUN IP */}
      <span className="fleet-tile-ip">{edgeTunIp || "—"}</span>

      {/* Last seen */}
      <span className="fleet-tile-seen">{ago(node.last_seen)}</span>
    </button>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

export default function NodeSummaryPanel({ nodes, nodesLoading, nodesError, proxyBase }) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name"); // "name" | "status" | "seen"
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "online" | "offline"

  const handleSelect = useCallback((nodeId) => {
    setSelectedNode((prev) => (prev === nodeId ? null : nodeId));
  }, []);

  const selectedData = useMemo(
    () => nodes.find((n) => n.node_id === selectedNode) || null,
    [nodes, selectedNode]
  );

  const onlineCount = nodes.filter((n) => !n.stale).length;

  // Filter and sort nodes
  const filteredNodes = useMemo(() => {
    let result = [...nodes];

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (n) =>
          (n.node_id || "").toLowerCase().includes(q) ||
          (n.tun_address || "").toLowerCase().includes(q) ||
          (n.api_url || "").toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter === "online") result = result.filter((n) => !n.stale);
    if (statusFilter === "offline") result = result.filter((n) => n.stale);

    // Sort
    result.sort((a, b) => {
      if (sortBy === "status") {
        if (a.stale !== b.stale) return a.stale ? 1 : -1;
        return (a.node_id || "").localeCompare(b.node_id || "");
      }
      if (sortBy === "seen") {
        const aTime = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        const bTime = b.last_seen ? new Date(b.last_seen).getTime() : 0;
        return bTime - aTime; // most recent first
      }
      return (a.node_id || "").localeCompare(b.node_id || "");
    });

    return result;
  }, [nodes, searchQuery, statusFilter, sortBy]);

  return (
    <div className="fleet-panel">
      {/* ── Counts ── */}
      <div className="fleet-counts">
        <span className="fleet-count">{nodes.length} nodes</span>
        <span className="fleet-count-sep">·</span>
        <span className="fleet-count online">{onlineCount} online</span>
        {nodes.length - onlineCount > 0 && (
          <>
            <span className="fleet-count-sep">·</span>
            <span className="fleet-count stale">{nodes.length - onlineCount} offline</span>
          </>
        )}
      </div>

      {/* ── Search & Filter toolbar ── */}
      {nodes.length > 0 && (
        <div className="fleet-toolbar">
          <div className="fleet-search-wrap">
            <FiSearch size={14} className="fleet-search-icon" />
            <input
              type="text"
              className="fleet-search-input"
              placeholder="Search nodes by name or IP…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search edge nodes"
            />
            {searchQuery && (
              <button
                className="fleet-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <FiX size={12} />
              </button>
            )}
          </div>
          <div className="fleet-filter-group">
            <FiFilter size={13} style={{ opacity: 0.5 }} />
            {["all", "online", "offline"].map((f) => (
              <button
                key={f}
                className={`fleet-filter-btn ${statusFilter === f ? "active" : ""}`}
                onClick={() => setStatusFilter(f)}
              >
                {f === "all" ? "All" : f === "online" ? "Online" : "Offline"}
              </button>
            ))}
          </div>
          <select
            className="fleet-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort nodes"
          >
            <option value="name">Sort: Name</option>
            <option value="status">Sort: Status</option>
            <option value="seen">Sort: Last Seen</option>
          </select>
        </div>
      )}

      {/* ── Loading / empty ── */}
      {nodesLoading && nodes.length === 0 && (
        <SkeletonTileGrid count={6} />
      )}
      {!nodesLoading && nodes.length === 0 && (
        <div className="fleet-empty">
          <EdgeIcon size={48} className="fleet-empty-icon" />
          <p>No edge controllers registered yet.</p>
        </div>
      )}

      {/* ── No search results ── */}
      {nodes.length > 0 && filteredNodes.length === 0 && (
        <div className="fleet-empty">
          <FiSearch size={36} style={{ opacity: 0.25 }} />
          <p>No nodes match "{searchQuery}"</p>
          <button
            className="fleet-filter-btn active"
            onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
            style={{ marginTop: 8 }}
          >
            Clear filters
          </button>
        </div>
      )}

      {nodesError && <div className="fleet-error">{nodesError}</div>}

      {/* ── Tile grid ── */}
      <div className="fleet-grid">
        {filteredNodes.map((node) => (
          <NodeTile
            key={node.node_id}
            node={node}
            isSelected={selectedNode === node.node_id}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {/* ── Detail pane (appears below tiles) ── */}
      {selectedData && (
        <WanNamesProvider nodeId={selectedNode}>
          <NodeDetail
            node={selectedData}
            proxyBase={proxyBase}
            onClose={() => setSelectedNode(null)}
          />
        </WanNamesProvider>
      )}
    </div>
  );
}
